import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { IdentityRow } from '@/modules/contacts/components/IdentityRow';
import { memoryCategoryLabelKey } from '@/modules/contacts/components/MemoryCard';
import { useContactIdentities } from '@/modules/contacts/hooks/useContactIdentities';
import { useContactMemories } from '@/modules/contacts/hooks/useContactMemories';
import { matchesContactIdentity } from '@/modules/contacts/utils/identity';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { Contact, ContactIdentity, ContactMemory } from '@/shared/types/contacts';
import { e164PhonePattern } from '@/util/validation/patterns';

import { useSessionContactId } from '../../../hooks/useSessionContactId';
import { useSessionContent } from '../../../hooks/useSessionContent';
import { selectSessionStart, selectTryItSessionId } from '../../../redux/selectors';
import { InfoTextRow, PanelSectionTitle } from '../../../shared/InfoRow';

const MemoriesSection = ({
  memories,
  isPending,
}: {
  memories: ContactMemory[];
  isPending: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3">
      <PanelSectionTitle>{t('contacts.memories')}</PanelSectionTitle>

      {isPending ? (
        <Skeleton className="h-16 w-full" />
      ) : (
        <div className="flex flex-col gap-4">
          {memories.map((memory) => (
            <div key={memory.memory_id} className="flex flex-col gap-1">
              <span className="text-xs leading-4 font-medium text-ai">
                {t(memoryCategoryLabelKey[memory.category])}
              </span>
              <p className="text-sm leading-5 font-normal wrap-anywhere text-foreground">
                {memory.content}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ContactInformationSection = ({
  contact,
  identities,
  isPending,
}: {
  contact: Contact;
  identities: ContactIdentity[];
  isPending: boolean;
}) => {
  const { t } = useTranslation();

  // No `primary` flag on identities — the contact's own email / phone are it.
  const isPrimary = (value: string) => matchesContactIdentity(contact, value);

  return (
    <div className="flex flex-col gap-3">
      <PanelSectionTitle>{t('contacts.contact_information')}</PanelSectionTitle>
      {isPending && <Skeleton className="h-16 w-full" />}
      <div className="flex w-full flex-col gap-1.5">
        {identities.map((identity) => (
          <IdentityRow
            key={identity.identity_id}
            identity={identity}
            primary={isPrimary(identity.value)}
            variant="compact"
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Replay-only User panel — the session's identity fields plus, for a known
 * contact, their memories, contact methods and a link to the full profile.
 */
export const UserPanel = () => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  const { contact, isResolving } = useSessionContactId();
  const { data: content, isLoading } = useSessionContent(sessionId, sessionStart);
  const { identities, status: identitiesStatus } = useContactIdentities(contact?.contact_id);
  const { memories, status: memoriesStatus } = useContactMemories(contact?.contact_id);

  const email = content?.user_email;
  const channelUserId = content?.channel_user_id;
  const callerPhone = channelUserId && e164PhonePattern.test(channelUserId) ? channelUserId : null;
  const channelUserIdDuplicatesPhone = !!callerPhone && callerPhone === channelUserId;
  // The email row is dropped only when the identities below really list it: the
  // contact can be matched by phone or external id, and the list can come back
  // empty (request failed, every identity soft-deleted).
  const emailInIdentities =
    !!email &&
    identities.some(
      (identity) => identity.value.trim().toLowerCase() === email.trim().toLowerCase()
    );
  // Held back until the contact and its identities settle, or the row would render
  // and then vanish as the Contact information section takes the email over.
  const identitiesPending = !!contact && identitiesStatus === 'pending';
  const showEmail = !isResolving && !identitiesPending && !emailInIdentities;
  const showContactInformation = !!contact && (identitiesPending || identities.length > 0);
  const memoriesPending = !!contact && memoriesStatus === 'pending';
  const showMemories = memoriesPending || memories.length > 0;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-3">
        <PanelSectionTitle>{t('try_it.user.info')}</PanelSectionTitle>
        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <div className="flex flex-col gap-4">
            <InfoTextRow
              label={t('try_it.user.name')}
              value={content?.user_name || t('common.anonymous')}
            />
            <InfoTextRow label={t('try_it.user.user_id')} value={content?.user_id} />
            <InfoTextRow
              label={t('try_it.user.channel_user_id')}
              value={channelUserIdDuplicatesPhone ? null : channelUserId}
            />
            <InfoTextRow label={t('try_it.user.phone')} value={callerPhone} />
            {showEmail && <InfoTextRow label={t('try_it.user.user_email')} value={email} />}
            <InfoTextRow
              label={t('try_it.user.external_user_id')}
              value={content?.external_user_id}
            />

            {contact && slug && (
              <Link
                to={`/${slug}/contacts/${contact.contact_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 self-start text-sm leading-none font-medium text-primary hover:underline"
              >
                {t('contacts.view_full_profile')}
                <ExternalLinkIcon className="size-4 shrink-0" aria-hidden />
              </Link>
            )}
          </div>
        )}
      </div>

      {isResolving && <Skeleton className="h-24 w-full" />}

      {showMemories && (
        <>
          <Separator />
          <MemoriesSection memories={memories} isPending={memoriesPending} />
        </>
      )}

      {contact && showContactInformation && (
        <>
          <Separator />
          <ContactInformationSection
            contact={contact}
            identities={identities}
            isPending={identitiesPending}
          />
        </>
      )}
    </div>
  );
};
